/**
 * Admin Service
 *
 * Comprehensive API service for superadmin operations:
 * - Tenant CRUD (create, read, update, delete)
 * - Tenant lifecycle (activate, suspend, cancel)
 * - Tenant schema provisioning
 * - Feature flags with tenant overrides
 * - System configuration management
 * - Notification templates
 * - Admin audit logs
 * - Admin dashboard stats
 */

import { apiClient } from '@/lib/api-client';
import { AUDIT_LOG_ACTION_LABELS, AUDIT_LOG_ENTITY_LABELS } from '../types';
import type {
  AdminTenant,
  GlobalAdminUserListResponse,
  PlatformPlan,
  PlatformPlanStats,
  PlatformPlanSummary,
  CreatePlatformPlanData,
  UpdatePlatformPlanData,
  SeedPlatformPlansResult,
  TenantInviteListResponse,
  TenantInviteRecord,
  TenantInviteStats,
  InviteTenantUserData,
  TenantInviteResult,
  BulkTenantInviteResult,
  TenantListFilters,
  TenantListResponse,
  TenantStats,
  CreateTenantData,
  UpdateTenantData,
  TenantAction,
  TenantLifecycleHistoryResponse,
  TenantLifecycleSummary,
  SuspendedTenantListResponse,
  TenantStatusDistribution,
  FeatureFlag,
  FeatureFlagValueType,
  FeatureFlagWithOverrides,
  CreateFeatureFlagData,
  UpdateFeatureFlagData,
  CreateFeatureFlagOverrideData,
  FeatureFlagOverride,
  SystemConfig,
  SystemConfigCategory,
  SystemConfigDataType,
  SystemConfigTenantOverride,
  SystemConfigTenantReference,
  CreateSystemConfigData,
  UpdateSystemConfigData,
  BulkSystemConfigUpsertResult,
  ConfigurationOption,
  CreateConfigurationOptionData,
  UpdateConfigurationOptionData,
  BulkConfigurationOptionResult,
  NotificationTemplate,
  NotificationTemplateChannel,
  CreateNotificationTemplateData,
  UpdateNotificationTemplateData,
  AuditLog,
  AuditLogFilters,
  AuditLogListResponse,
  AuthAuditStats,
  AuthSecurityAlerts,
} from '../types';

// Base path for admin service endpoints via gateway
const ADMIN_BASE = '/admin';
const AUTH_ADMIN_BASE = '/auth/admin/auth';

// Direct admin endpoints (feature flags, config, etc. don't have /admin prefix in their paths)
const FEATURE_FLAGS_BASE = '/admin/feature-flags';
const CONFIG_BASE = '/admin/config';
const CONFIG_OPTIONS_BASE = '/admin/config-options';
const TEMPLATES_BASE = '/admin/notification-templates';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function unwrapAdminEnvelope<T>(response: T | { success: boolean; data: T }): T {
  if (isRecord(response) && typeof response['success'] === 'boolean' && 'data' in response) {
    return response['data'];
  }
  return response as T;
}

function unsupportedAdminApiOperation(): never {
  throw new Error('This operation is not supported by the current admin API');
}

interface AuthAuditLogEntry {
  id: string;
  userId: string | null;
  userEmail?: string | null;
  userName?: string | null;
  tenantId: string | null;
  tenantName?: string | null;
  action: string;
  status: string;
  ipAddress: string | null;
  userAgent: string | null;
  country?: string | null;
  city?: string | null;
  sessionId: string | null;
  targetUserId?: string | null;
  targetUserEmail?: string | null;
  targetUserName?: string | null;
  errorMessage?: string | null;
  details: Record<string, unknown> | null;
  createdAt: string | Date;
}

interface AuthAuditLogListResponse {
  logs: AuthAuditLogEntry[];
  total: number;
  hasMore: boolean;
}

interface AuthAuditLogExportResponse {
  logs: AuthAuditLogEntry[];
  count: number;
}

interface AuthAuditCleanupResponse {
  success: boolean;
  deletedCount: number;
  message: string;
}

function tenantAuthAdminPath(tenantId: string, path: string): string {
  return `${AUTH_ADMIN_BASE}/tenants/${encodeURIComponent(tenantId)}/${path}`;
}

function appendDefinedParam(params: URLSearchParams, key: string, value: unknown): void {
  if (value !== undefined && value !== null && value !== '') {
    params.append(key, String(value));
  }
}

function withQuery(path: string, params: URLSearchParams): string {
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

function mapAuthAuditLog(log: AuthAuditLogEntry): AuditLog {
  const details = isRecord(log.details) ? log.details : {};
  const oldValue = details['oldValue'] ?? details['previousValue'];
  const newValue = details['newValue'];
  const entityType = log.sessionId ? 'SESSION' : log.targetUserId || log.userId ? 'USER' : 'TENANT';
  const entityId = log.sessionId ?? log.targetUserId ?? log.userId ?? log.tenantId ?? log.id;
  const metadata = {
    ...details,
    ...(log.targetUserId ? { targetUserId: log.targetUserId } : {}),
    ...(log.sessionId ? { sessionId: log.sessionId } : {}),
    ...(log.errorMessage ? { errorMessage: log.errorMessage } : {}),
    ...(log.country ? { country: log.country } : {}),
    ...(log.city ? { city: log.city } : {}),
  };

  return {
    id: log.id,
    action: log.action,
    status: log.status,
    entityType,
    entityId,
    userId: log.userId ?? undefined,
    userEmail: log.userEmail ?? undefined,
    userName: log.userName ?? undefined,
    tenantId: log.tenantId ?? undefined,
    tenantName: log.tenantName ?? undefined,
    targetUserId: log.targetUserId ?? undefined,
    targetUserEmail: log.targetUserEmail ?? undefined,
    targetUserName: log.targetUserName ?? undefined,
    sessionId: log.sessionId ?? undefined,
    errorMessage: log.errorMessage ?? undefined,
    country: log.country ?? undefined,
    city: log.city ?? undefined,
    oldValue,
    newValue,
    metadata,
    ipAddress: log.ipAddress ?? undefined,
    userAgent: log.userAgent ?? undefined,
    createdAt: new Date(log.createdAt),
  };
}

function csvValue(value: unknown): string {
  const text = typeof value === 'string' ? value : JSON.stringify(value ?? '');
  return `"${text.replace(/"/g, '""')}"`;
}

function buildAuditCsv(logs: AuthAuditLogEntry[]): string {
  const headers = [
    'id',
    'createdAt',
    'action',
    'status',
    'userId',
    'userName',
    'userEmail',
    'tenantId',
    'tenantName',
    'targetUserId',
    'targetUserName',
    'targetUserEmail',
    'ipAddress',
    'sessionId',
    'details',
  ];
  const rows = logs.map((log) =>
    [
      log.id,
      log.createdAt,
      log.action,
      log.status,
      log.userId,
      log.userName,
      log.userEmail,
      log.tenantId,
      log.tenantName,
      log.targetUserId,
      log.targetUserName,
      log.targetUserEmail,
      log.ipAddress,
      log.sessionId,
      log.details,
    ]
      .map(csvValue)
      .join(','),
  );

  return [headers.join(','), ...rows].join('\n');
}

async function getTenantById(id: string): Promise<AdminTenant> {
  return apiClient.get<AdminTenant>(`${ADMIN_BASE}/superadmin/tenants/${id}`);
}

function tenantLifecycleBase(tenantId: string): string {
  return `${ADMIN_BASE}/superadmin/tenants/${encodeURIComponent(tenantId)}/lifecycle`;
}

async function runTenantLifecycleAction(
  id: string,
  action: 'suspend' | 'reactivate' | 'status',
  body: Record<string, unknown>,
): Promise<AdminTenant> {
  await apiClient.post(`${tenantLifecycleBase(id)}/${action}`, body);
  return getTenantById(id);
}

// =============================================================================
// TENANT MANAGEMENT API (Superadmin)
// =============================================================================

export const tenantsApi = {
  /**
   * List all tenants with filtering and pagination
   */
  list: async (filters?: TenantListFilters): Promise<TenantListResponse> => {
    const params = new URLSearchParams();
    if (filters?.search) params.append('search', filters.search);
    if (filters?.status && filters.status !== 'all') params.append('status', filters.status);
    if (filters?.plan && filters.plan !== 'all') params.append('plan', filters.plan);
    if (filters?.country) params.append('country', filters.country);
    if (filters?.sortBy) params.append('sortBy', filters.sortBy);
    if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder);
    if (filters?.page) params.append('page', String(filters.page));
    if (filters?.limit) params.append('limit', String(filters.limit));

    const response = await apiClient.get<TenantListResponse>(
      `${ADMIN_BASE}/superadmin/tenants?${params}`,
    );
    return response;
  },

  /**
   * Get tenant by ID
   */
  getById: async (id: string): Promise<AdminTenant> => {
    return getTenantById(id);
  },

  /**
   * Get tenant by slug
   */
  getBySlug: async (slug: string): Promise<AdminTenant> => {
    const response = await apiClient.get<AdminTenant>(
      `${ADMIN_BASE}/superadmin/tenants/slug/${slug}`,
    );
    return response;
  },

  /**
   * Get tenant statistics and metrics
   */
  getStats: async (): Promise<TenantStats> => {
    const response = await apiClient.get<TenantStats>(
      `${ADMIN_BASE}/superadmin/tenants/stats/summary`,
    );
    return response;
  },

  /**
   * Get current tenant status distribution for the platform.
   */
  getStatusDistribution: async (): Promise<TenantStatusDistribution> => {
    return apiClient.get<TenantStatusDistribution>(
      `${ADMIN_BASE}/superadmin/tenants-status/distribution`,
    );
  },

  /**
   * Get suspended tenants from the lifecycle side-controller.
   */
  getSuspendedTenants: async (filters?: {
    limit?: number;
    offset?: number;
  }): Promise<SuspendedTenantListResponse> => {
    const params = new URLSearchParams();
    appendDefinedParam(params, 'limit', filters?.limit);
    appendDefinedParam(params, 'offset', filters?.offset);
    return apiClient.get<SuspendedTenantListResponse>(
      withQuery(`${ADMIN_BASE}/superadmin/tenants-status/suspended`, params),
    );
  },

  /**
   * Create a new tenant (full provisioning)
   */
  create: async (data: CreateTenantData): Promise<AdminTenant> => {
    const response = await apiClient.post<AdminTenant>(`${ADMIN_BASE}/superadmin/tenants`, data);
    return response;
  },

  /**
   * Update tenant information
   */
  update: async (id: string, data: UpdateTenantData): Promise<AdminTenant> => {
    const response = await apiClient.put<AdminTenant>(
      `${ADMIN_BASE}/superadmin/tenants/${id}`,
      data,
    );
    return response;
  },

  /**
   * Delete tenant (soft delete)
   */
  delete: async (id: string, purge = false): Promise<void> => {
    const params = purge ? '?purge=true' : '';
    await apiClient.delete(`${ADMIN_BASE}/superadmin/tenants/${id}${params}`);
  },

  /**
   * Activate tenant
   */
  activate: async (id: string, reason?: string): Promise<AdminTenant> => {
    return runTenantLifecycleAction(id, 'reactivate', {
      reason: reason?.trim() || 'Reactivated by platform admin',
    });
  },

  /**
   * Suspend tenant
   */
  suspend: async (id: string, reason?: string): Promise<AdminTenant> => {
    return runTenantLifecycleAction(id, 'suspend', {
      reason: 'OTHER',
      notes: reason ?? 'Suspended by platform admin',
    });
  },

  /**
   * Cancel tenant subscription
   */
  cancel: async (id: string, reason?: string): Promise<AdminTenant> => {
    return runTenantLifecycleAction(id, 'status', {
      newStatus: 'INACTIVE',
      reason: reason ?? 'Cancelled by platform admin',
    });
  },

  /**
   * Get tenant lifecycle history.
   */
  getLifecycleHistory: async (
    id: string,
    filters?: { limit?: number; offset?: number },
  ): Promise<TenantLifecycleHistoryResponse> => {
    const params = new URLSearchParams();
    appendDefinedParam(params, 'limit', filters?.limit);
    appendDefinedParam(params, 'offset', filters?.offset);
    return apiClient.get<TenantLifecycleHistoryResponse>(
      withQuery(`${tenantLifecycleBase(id)}/history`, params),
    );
  },

  /**
   * Get tenant lifecycle summary.
   */
  getLifecycleSummary: async (id: string): Promise<TenantLifecycleSummary> => {
    return apiClient.get<TenantLifecycleSummary>(`${tenantLifecycleBase(id)}/summary`);
  },

  /**
   * Perform generic tenant action
   */
  performAction: async (id: string, action: TenantAction): Promise<AdminTenant> => {
    if (action.type === 'suspend') {
      return tenantsApi.suspend(id, action.reason);
    }
    if (action.type === 'activate') {
      return tenantsApi.activate(id, action.reason);
    }
    if (action.type === 'cancel') {
      return tenantsApi.cancel(id, action.reason);
    }
    throw new Error(`Tenant action "${action.type}" is not supported by the current admin API`);
  },

  /**
   * Impersonate tenant (get access token)
   */
  impersonate: async (
    id: string,
  ): Promise<{
    accessToken: string;
    expiresAt: Date;
    user: {
      id: string;
      email: string;
      firstName: string;
      lastName: string;
      tenantId: string;
      role: string;
      permissions: string[];
    };
  }> => {
    const response = await apiClient.post<{
      accessToken: string;
      expiresAt: Date;
      user: {
        id: string;
        email: string;
        firstName: string;
        lastName: string;
        tenantId: string;
        role: string;
        permissions: string[];
      };
    }>(`${ADMIN_BASE}/superadmin/tenants/${id}/impersonate`);
    return response;
  },

  /**
   * Export tenants to CSV/Excel
   */
  exportTenants: async (filters?: TenantListFilters): Promise<Blob> => {
    const params = new URLSearchParams();
    if (filters?.status && filters.status !== 'all') params.append('status', filters.status);
    if (filters?.plan && filters.plan !== 'all') params.append('plan', filters.plan);

    const response = await apiClient.get<Blob>(
      `${ADMIN_BASE}/superadmin/tenants/export?${params}`,
      {
        responseType: 'blob',
      },
    );
    return response;
  },
};

// =============================================================================
// PLATFORM USER API (Superadmin)
// =============================================================================

export const globalUsersApi = {
  list: async (filters?: {
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<GlobalAdminUserListResponse> => {
    const params = new URLSearchParams();
    appendDefinedParam(params, 'search', filters?.search);
    appendDefinedParam(params, 'limit', filters?.limit);
    appendDefinedParam(params, 'offset', filters?.offset);

    return apiClient.get<GlobalAdminUserListResponse>(
      withQuery(`${ADMIN_BASE}/superadmin/users`, params),
    );
  },
};

// =============================================================================
// PLATFORM PLAN API (Superadmin)
// =============================================================================

const PLANS_BASE = `${ADMIN_BASE}/superadmin/plans`;

export const plansApi = {
  list: async (includeInactive = true): Promise<PlatformPlan[]> => {
    const params = new URLSearchParams();
    if (includeInactive) {
      params.set('includeInactive', 'true');
    }
    return apiClient.get<PlatformPlan[]>(withQuery(PLANS_BASE, params));
  },

  publicList: async (): Promise<PlatformPlanSummary[]> => {
    return apiClient.get<PlatformPlanSummary[]>(`${PLANS_BASE}/public`);
  },

  getStats: async (): Promise<PlatformPlanStats> => {
    return apiClient.get<PlatformPlanStats>(`${PLANS_BASE}/stats`);
  },

  getDefault: async (): Promise<PlatformPlan | null> => {
    return apiClient.get<PlatformPlan | null>(`${PLANS_BASE}/default`);
  },

  create: async (data: CreatePlatformPlanData): Promise<PlatformPlan> => {
    return apiClient.post<PlatformPlan>(PLANS_BASE, data);
  },

  update: async (id: string, data: UpdatePlatformPlanData): Promise<PlatformPlan> => {
    return apiClient.put<PlatformPlan>(`${PLANS_BASE}/${encodeURIComponent(id)}`, data);
  },

  deactivate: async (id: string): Promise<void> => {
    await apiClient.delete(`${PLANS_BASE}/${encodeURIComponent(id)}`);
  },

  seedDefaults: async (): Promise<SeedPlatformPlansResult> => {
    return apiClient.post<SeedPlatformPlansResult>(`${PLANS_BASE}/seed`);
  },
};

// =============================================================================
// TENANT INVITATION API (Superadmin)
// =============================================================================

function tenantInvitesBase(tenantId: string): string {
  return `${ADMIN_BASE}/superadmin/tenants/${encodeURIComponent(tenantId)}/invites`;
}

export const tenantInvitesApi = {
  list: async (
    tenantId: string,
    filters?: { limit?: number; offset?: number },
  ): Promise<TenantInviteListResponse> => {
    const params = new URLSearchParams();
    appendDefinedParam(params, 'limit', filters?.limit);
    appendDefinedParam(params, 'offset', filters?.offset);
    return apiClient.get<TenantInviteListResponse>(withQuery(tenantInvitesBase(tenantId), params));
  },

  pending: async (tenantId: string): Promise<TenantInviteRecord[]> => {
    return apiClient.get<TenantInviteRecord[]>(`${tenantInvitesBase(tenantId)}/pending`);
  },

  stats: async (tenantId: string): Promise<TenantInviteStats> => {
    return apiClient.get<TenantInviteStats>(`${tenantInvitesBase(tenantId)}/stats`);
  },

  detail: async (tenantId: string, inviteId: string): Promise<TenantInviteRecord> => {
    return apiClient.get<TenantInviteRecord>(
      `${tenantInvitesBase(tenantId)}/${encodeURIComponent(inviteId)}`,
    );
  },

  invite: async (tenantId: string, data: InviteTenantUserData): Promise<TenantInviteResult> => {
    return apiClient.post<TenantInviteResult>(tenantInvitesBase(tenantId), data);
  },

  bulkInvite: async (
    tenantId: string,
    invites: InviteTenantUserData[],
  ): Promise<BulkTenantInviteResult> => {
    return apiClient.post<BulkTenantInviteResult>(`${tenantInvitesBase(tenantId)}/bulk`, {
      invites,
    });
  },

  resend: async (tenantId: string, inviteId: string): Promise<TenantInviteResult> => {
    return apiClient.post<TenantInviteResult>(
      `${tenantInvitesBase(tenantId)}/${encodeURIComponent(inviteId)}/resend`,
    );
  },

  revoke: async (tenantId: string, inviteId: string): Promise<void> => {
    await apiClient.delete(`${tenantInvitesBase(tenantId)}/${encodeURIComponent(inviteId)}`);
  },
};

// =============================================================================
// TENANT SCHEMA PROVISIONING API
// =============================================================================

export const schemaApi = {
  /**
   * Provision tenant schema (create database schema)
   */
  provision: async (
    tenantId: string,
  ): Promise<{
    message: string;
    jobId: string;
  }> => {
    void tenantId;
    return unsupportedAdminApiOperation();
  },

  /**
   * Run schema migrations for tenant
   */
  migrate: async (
    tenantId: string,
    version?: string,
  ): Promise<{
    migrationsApplied: number;
    status: string;
  }> => {
    void tenantId;
    void version;
    return unsupportedAdminApiOperation();
  },

  /**
   * Get schema provisioning status
   */
  getStatus: async (
    tenantId: string,
  ): Promise<{
    isProvisioned: boolean;
    schemaVersion: string;
    lastMigration?: Date;
    pendingMigrations: number;
  }> => {
    void tenantId;
    return unsupportedAdminApiOperation();
  },

  /**
   * Rollback schema to previous version
   */
  rollback: async (
    tenantId: string,
    targetVersion?: string,
  ): Promise<{
    success: boolean;
    currentVersion: string;
  }> => {
    void tenantId;
    void targetVersion;
    return unsupportedAdminApiOperation();
  },
};

// =============================================================================
// FEATURE FLAGS API
// =============================================================================

// Helper to map backend FeatureFlag response to frontend format
interface BackendFeatureFlag {
  id: string;
  tenantId: string | null;
  featureKey: string;
  featureName: string;
  description: string | null;
  enabled: boolean;
  rolloutPercentage: number;
  conditions: unknown;
  metadata: unknown;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
  expiresAt: string | null;
  tenant: { id: string; code: string; name: string } | null;
}

function mapFeatureFlag(flag: BackendFeatureFlag): FeatureFlagWithOverrides {
  const metadata = flag.metadata as
    | { valueType?: FeatureFlagValueType; defaultValue?: unknown }
    | null
    | undefined;
  const expiresAt = flag.expiresAt ? new Date(flag.expiresAt) : undefined;

  return {
    id: flag.id,
    key: flag.featureKey,
    name: flag.featureName,
    description: flag.description ?? undefined,
    scope: flag.tenantId ? 'tenant' : 'global',
    tenantId: flag.tenantId,
    tenantName: flag.tenant?.name,
    tenantCode: flag.tenant?.code,
    valueType: metadata?.valueType ?? 'boolean',
    defaultValue: metadata?.defaultValue ?? flag.enabled,
    rolloutPercentage: flag.rolloutPercentage,
    isEnabled: flag.enabled,
    createdAt: new Date(flag.createdAt),
    updatedAt: new Date(flag.updatedAt),
    ...(expiresAt ? { expiresAt } : {}),
    overrides: [],
    tenantCount: 0,
  };
}

function mapFeatureFlagCreatePayload(data: CreateFeatureFlagData): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    description: data.description,
    enabled: data.isEnabled,
    featureKey: data.key,
    featureName: data.name,
    metadata: {
      defaultValue: data.defaultValue,
      scope: data.scope,
      valueType: data.valueType,
    },
  };

  if (data.scope === 'tenant' && data.tenantId) {
    payload['tenantId'] = data.tenantId;
  }

  return payload;
}

function mapFeatureFlagUpdatePayload(data: UpdateFeatureFlagData): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  if (data.name !== undefined) {
    payload['featureName'] = data.name;
  }
  if (data.description !== undefined) {
    payload['description'] = data.description;
  }
  if (data.isEnabled !== undefined) {
    payload['enabled'] = data.isEnabled;
  }
  if (data.defaultValue !== undefined) {
    payload['metadata'] = { defaultValue: data.defaultValue };
  }
  return payload;
}

function unsupportedFeatureFlagOverrides(): never {
  throw new Error('Feature flag tenant overrides are not supported by the current admin API');
}

export interface FeatureFlagCheckResult {
  featureKey: string;
  enabled: boolean;
  tenantId: string | null;
}

export const featureFlagsApi = {
  /**
   * List all feature flags
   */
  list: async (): Promise<FeatureFlagWithOverrides[]> => {
    const response = await apiClient.get<BackendFeatureFlag[]>(FEATURE_FLAGS_BASE);
    return response.map(mapFeatureFlag);
  },

  /**
   * Get global feature flags only
   */
  getGlobal: async (): Promise<FeatureFlag[]> => {
    const response = await apiClient.get<BackendFeatureFlag[]>(`${FEATURE_FLAGS_BASE}/global`);
    return response.map(mapFeatureFlag);
  },

  /**
   * Get tenant-scoped feature flags
   */
  getTenant: async (tenantId: string): Promise<FeatureFlag[]> => {
    const response = await apiClient.get<BackendFeatureFlag[]>(
      `${FEATURE_FLAGS_BASE}/tenant/${tenantId}`,
    );
    return response.map((flag) =>
      mapFeatureFlag({
        ...flag,
        tenantId: flag.tenantId ?? tenantId,
      }),
    );
  },

  /**
   * Get feature flag by ID
   */
  getById: async (id: string): Promise<FeatureFlagWithOverrides> => {
    const response = await apiClient.get<BackendFeatureFlag>(`${FEATURE_FLAGS_BASE}/${id}`);
    return mapFeatureFlag(response);
  },

  /**
   * Get feature flag by key
   */
  getByKey: async (key: string): Promise<FeatureFlagWithOverrides> => {
    const response = await apiClient.get<BackendFeatureFlag[]>(
      `${FEATURE_FLAGS_BASE}?featureKey=${encodeURIComponent(key)}`,
    );
    const flags = response;
    const flag =
      flags.find((candidate) => candidate.featureKey === key) ??
      flags.find((candidate) => candidate.featureKey.toLowerCase() === key.toLowerCase());
    if (!flag) {
      throw new Error(`Feature flag "${key}" not found`);
    }
    return mapFeatureFlag(flag);
  },

  /**
   * Check if feature flag is enabled
   */
  check: async (key: string, tenantId?: string): Promise<FeatureFlagCheckResult> => {
    const params = tenantId ? `?tenantId=${tenantId}` : '';
    const response = await apiClient.get<FeatureFlagCheckResult>(
      `${FEATURE_FLAGS_BASE}/check/${key}${params}`,
    );
    return response;
  },

  /**
   * Create new feature flag
   */
  create: async (data: CreateFeatureFlagData): Promise<FeatureFlag> => {
    const response = await apiClient.post<BackendFeatureFlag>(
      FEATURE_FLAGS_BASE,
      mapFeatureFlagCreatePayload(data),
    );
    return mapFeatureFlag(response);
  },

  /**
   * Update feature flag
   */
  update: async (id: string, data: UpdateFeatureFlagData): Promise<FeatureFlag> => {
    const response = await apiClient.put<BackendFeatureFlag>(
      `${FEATURE_FLAGS_BASE}/${id}`,
      mapFeatureFlagUpdatePayload(data),
    );
    return mapFeatureFlag(response);
  },

  /**
   * Delete feature flag
   */
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`${FEATURE_FLAGS_BASE}/${id}`);
  },

  /**
   * Toggle feature flag on/off
   */
  toggle: async (id: string, isEnabled: boolean): Promise<FeatureFlag> => {
    const response = await apiClient.post<BackendFeatureFlag>(
      `${FEATURE_FLAGS_BASE}/${id}/toggle`,
      { isEnabled },
    );
    return mapFeatureFlag(response);
  },

  // Tenant Overrides
  /**
   * Create tenant-specific override
   */
  createOverride: async (
    flagId: string,
    data: CreateFeatureFlagOverrideData,
  ): Promise<FeatureFlagOverride> => {
    void flagId;
    void data;
    return unsupportedFeatureFlagOverrides();
  },

  /**
   * Update tenant override
   */
  updateOverride: async (
    flagId: string,
    overrideId: string,
    data: Partial<CreateFeatureFlagOverrideData>,
  ): Promise<FeatureFlagOverride> => {
    void flagId;
    void overrideId;
    void data;
    return unsupportedFeatureFlagOverrides();
  },

  /**
   * Delete tenant override
   */
  deleteOverride: async (flagId: string, overrideId: string): Promise<void> => {
    void flagId;
    void overrideId;
    unsupportedFeatureFlagOverrides();
  },

  /**
   * Get all overrides for a feature flag
   */
  getOverrides: async (flagId: string): Promise<FeatureFlagOverride[]> => {
    void flagId;
    return unsupportedFeatureFlagOverrides();
  },
};

// =============================================================================
// SYSTEM CONFIG API
// =============================================================================

// Helper to map backend SystemConfig response to frontend format
interface BackendSystemConfig {
  id: string;
  category: string;
  key: string;
  value: unknown;
  dataType: SystemConfigDataType;
  description: string | null;
  editableByTenant: boolean;
  validationRules: unknown;
  defaultValue: unknown;
  isActive: boolean;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
  tenantOverrides?: BackendSystemConfigTenantOverride[];
}

interface BackendSystemConfigTenant {
  id: string;
  code: string | null;
  name: string | null;
}

interface BackendSystemConfigSummary {
  category: string;
  key: string;
  dataType: SystemConfigDataType;
  description: string | null;
  defaultValue?: unknown;
}

interface BackendSystemConfigTenantOverride {
  id: string;
  tenantId: string;
  systemConfigId?: string;
  value: unknown;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  tenant?: BackendSystemConfigTenant;
  systemConfig?: BackendSystemConfigSummary;
}

interface BackendTenantConfigReference extends BackendSystemConfigTenantOverride {
  tenant: BackendSystemConfigTenant;
}

function mapSystemConfigTenantOverride(
  override: BackendSystemConfigTenantOverride,
): SystemConfigTenantOverride {
  return {
    id: override.id,
    tenantId: override.tenantId,
    tenantName: override.tenant?.name ?? undefined,
    tenantCode: override.tenant?.code ?? undefined,
    systemConfigId: override.systemConfigId,
    category: override.systemConfig?.category,
    key: override.systemConfig?.key,
    value: override.value,
    defaultValue: override.systemConfig?.defaultValue,
    dataType: override.systemConfig?.dataType,
    description: override.systemConfig?.description ?? undefined,
    isActive: override.isActive,
    createdAt: override.createdAt ? new Date(override.createdAt) : undefined,
    updatedAt: override.updatedAt ? new Date(override.updatedAt) : undefined,
  };
}

function mapSystemConfigTenantReference(
  override: BackendTenantConfigReference,
): SystemConfigTenantReference {
  return {
    id: override.id,
    tenantId: override.tenantId,
    tenantName: override.tenant.name ?? undefined,
    tenantCode: override.tenant.code ?? undefined,
    value: override.value,
    isActive: override.isActive ?? true,
    createdAt: override.createdAt ? new Date(override.createdAt) : new Date(0),
    updatedAt: override.updatedAt ? new Date(override.updatedAt) : new Date(0),
  };
}

function mapSystemConfig(config: BackendSystemConfig): SystemConfig {
  return {
    id: config.id,
    key: config.key,
    value: config.value,
    dataType: config.dataType,
    description: config.description ?? undefined,
    category: config.category,
    isSecret: false, // Backend doesn't have isSecret field currently
    editableByTenant: config.editableByTenant,
    validationRules: config.validationRules,
    defaultValue: config.defaultValue,
    isActive: config.isActive,
    tenantOverrides: (config.tenantOverrides ?? []).map(mapSystemConfigTenantOverride),
    updatedAt: new Date(config.updatedAt),
    updatedBy: config.updatedBy ?? undefined,
  };
}

function groupConfigsByCategory(configs: BackendSystemConfig[]): SystemConfigCategory[] {
  const grouped = configs.reduce(
    (acc, config) => {
      if (!acc[config.category]) {
        acc[config.category] = [];
      }
      acc[config.category].push(mapSystemConfig(config));
      return acc;
    },
    {} as Record<string, SystemConfig[]>,
  );

  return Object.entries(grouped).map(([category, configs]) => ({
    category,
    configs,
  }));
}

async function listBackendSystemConfigs(): Promise<BackendSystemConfig[]> {
  return apiClient.get<BackendSystemConfig[]>(CONFIG_BASE);
}

async function getBackendSystemConfigByKey(key: string): Promise<BackendSystemConfig> {
  const configs = await listBackendSystemConfigs();
  const config = configs.find((candidate) => candidate.key === key);
  if (!config) {
    throw new Error(`Configuration "${key}" not found`);
  }
  return config;
}

function unsupportedSystemConfigOperation(): never {
  throw new Error('This system configuration operation is not supported by the current admin API');
}

export const systemConfigApi = {
  /**
   * List all system configurations grouped by category
   */
  list: async (): Promise<SystemConfigCategory[]> => {
    return groupConfigsByCategory(await listBackendSystemConfigs());
  },

  /**
   * Get configs by category
   */
  getByCategory: async (category: string): Promise<SystemConfig[]> => {
    const response = await apiClient.get<BackendSystemConfig[]>(
      `${CONFIG_BASE}/categories/${category}`,
    );
    return response.map(mapSystemConfig);
  },

  /**
   * Get single config by key
   */
  getByKey: async (key: string): Promise<SystemConfig> => {
    return mapSystemConfig(await getBackendSystemConfigByKey(key));
  },

  /**
   * Get single config by backend id
   */
  getById: async (id: string): Promise<SystemConfig> => {
    const response = await apiClient.get<BackendSystemConfig>(`${CONFIG_BASE}/${id}`);
    return mapSystemConfig(response);
  },

  /**
   * Create new config
   */
  create: async (data: CreateSystemConfigData): Promise<SystemConfig> => {
    const response = await apiClient.post<BackendSystemConfig>(CONFIG_BASE, data);
    return mapSystemConfig(response);
  },

  /**
   * Update config value
   */
  update: async (key: string, value: unknown): Promise<SystemConfig> => {
    const config = await getBackendSystemConfigByKey(key);
    const response = await apiClient.put<BackendSystemConfig>(`${CONFIG_BASE}/${config.id}`, {
      value,
    });
    return mapSystemConfig(response);
  },

  updateById: async (id: string, data: UpdateSystemConfigData): Promise<SystemConfig> => {
    const response = await apiClient.put<BackendSystemConfig>(`${CONFIG_BASE}/${id}`, data);
    return mapSystemConfig(response);
  },

  /**
   * Update config by key
   */
  updateByKey: async (key: string, value: unknown): Promise<SystemConfig> => {
    return systemConfigApi.update(key, value);
  },

  /**
   * Delete config
   */
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`${CONFIG_BASE}/${id}`);
  },

  /**
   * Upsert configs through the backend bulk endpoint.
   */
  bulkUpsert: async (configs: CreateSystemConfigData[]): Promise<BulkSystemConfigUpsertResult> => {
    return apiClient.post<BulkSystemConfigUpsertResult>(`${CONFIG_BASE}/bulk-upsert`, {
      configs,
    });
  },

  /**
   * Bulk update multiple configs
   */
  bulkUpdate: async (configs: Array<{ key: string; value: unknown }>): Promise<SystemConfig[]> => {
    return Promise.all(configs.map((config) => systemConfigApi.update(config.key, config.value)));
  },

  /**
   * Reset config to default value
   */
  reset: async (key: string): Promise<SystemConfig> => {
    void key;
    return unsupportedSystemConfigOperation();
  },

  /**
   * Create a tenant-specific override for an overrideable system config.
   */
  createTenantOverride: async (data: {
    tenantId: string;
    systemConfigId: string;
    value: unknown;
  }): Promise<SystemConfigTenantOverride> => {
    const response = await apiClient.post<BackendSystemConfigTenantOverride>(
      `${CONFIG_BASE}/tenant/${data.tenantId}/override`,
      {
        systemConfigId: data.systemConfigId,
        value: data.value,
      },
    );
    return mapSystemConfigTenantOverride(response);
  },

  getTenantOverrides: async (tenantId: string): Promise<SystemConfigTenantOverride[]> => {
    const response = await apiClient.get<BackendSystemConfigTenantOverride[]>(
      `${CONFIG_BASE}/tenant/${tenantId}/overrides`,
    );
    return response.map(mapSystemConfigTenantOverride);
  },

  updateTenantOverride: async (
    overrideId: string,
    data: { value?: unknown; isActive?: boolean },
  ): Promise<SystemConfigTenantOverride> => {
    const response = await apiClient.put<BackendSystemConfigTenantOverride>(
      `${CONFIG_BASE}/override/${overrideId}`,
      data,
    );
    return mapSystemConfigTenantOverride(response);
  },

  deleteTenantOverride: async (overrideId: string): Promise<void> => {
    await apiClient.delete(`${CONFIG_BASE}/override/${overrideId}`);
  },

  deleteTenantOverrideByConfig: async (tenantId: string, systemConfigId: string): Promise<void> => {
    await apiClient.delete(`${CONFIG_BASE}/tenant/${tenantId}/override/${systemConfigId}`);
  },

  getTenantsWithOverride: async (
    systemConfigId: string,
  ): Promise<SystemConfigTenantReference[]> => {
    const response = await apiClient.get<BackendTenantConfigReference[]>(
      `${CONFIG_BASE}/system/${systemConfigId}/tenants`,
    );
    return response.map(mapSystemConfigTenantReference);
  },
};

// =============================================================================
// CONFIGURATION OPTIONS API
// =============================================================================

interface BackendConfigurationOption {
  id: string;
  category: string;
  optionKey: string;
  optionValue: string;
  description?: string | null;
  metadata?: unknown;
  displayOrder: number;
  isActive: boolean;
  isSystem: boolean;
  tenantId?: string | null;
  tenant?: {
    id: string;
    code?: string | null;
    name?: string | null;
  } | null;
  createdAt: string;
  updatedAt: string;
}

function mapConfigurationOption(option: BackendConfigurationOption): ConfigurationOption {
  return {
    id: option.id,
    category: option.category,
    optionKey: option.optionKey,
    optionValue: option.optionValue,
    description: option.description ?? undefined,
    metadata: option.metadata,
    displayOrder: option.displayOrder,
    isActive: option.isActive,
    isSystem: option.isSystem,
    tenantId: option.tenantId ?? null,
    tenantName: option.tenant?.name ?? undefined,
    tenantCode: option.tenant?.code ?? undefined,
    createdAt: new Date(option.createdAt),
    updatedAt: new Date(option.updatedAt),
  };
}

export const configurationOptionsApi = {
  list: async (params?: {
    category?: string;
    isActive?: boolean;
    tenantId?: string;
  }): Promise<ConfigurationOption[]> => {
    const query = new URLSearchParams();
    appendDefinedParam(query, 'category', params?.category);
    if (params?.isActive !== undefined) {
      query.append('isActive', String(params.isActive));
    }
    appendDefinedParam(query, 'tenantId', params?.tenantId);

    const suffix = query.toString() ? `?${query}` : '';
    const response = await apiClient.get<BackendConfigurationOption[]>(
      `${CONFIG_OPTIONS_BASE}${suffix}`,
    );
    return response.map(mapConfigurationOption);
  },

  categories: async (): Promise<string[]> => {
    const response = await apiClient.get<{ categories: string[] }>(
      `${CONFIG_OPTIONS_BASE}/categories`,
    );
    return response.categories;
  },

  getByCategory: async (category: string, tenantId?: string): Promise<ConfigurationOption[]> => {
    const query = new URLSearchParams();
    appendDefinedParam(query, 'tenantId', tenantId);
    const suffix = query.toString() ? `?${query}` : '';
    const response = await apiClient.get<BackendConfigurationOption[]>(
      `${CONFIG_OPTIONS_BASE}/category/${encodeURIComponent(category)}${suffix}`,
    );
    return response.map(mapConfigurationOption);
  },

  getById: async (id: string): Promise<ConfigurationOption> => {
    const response = await apiClient.get<BackendConfigurationOption>(
      `${CONFIG_OPTIONS_BASE}/${id}`,
    );
    return mapConfigurationOption(response);
  },

  create: async (data: CreateConfigurationOptionData): Promise<ConfigurationOption> => {
    const response = await apiClient.post<BackendConfigurationOption>(CONFIG_OPTIONS_BASE, data);
    return mapConfigurationOption(response);
  },

  update: async (id: string, data: UpdateConfigurationOptionData): Promise<ConfigurationOption> => {
    const response = await apiClient.put<BackendConfigurationOption>(
      `${CONFIG_OPTIONS_BASE}/${id}`,
      data,
    );
    return mapConfigurationOption(response);
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`${CONFIG_OPTIONS_BASE}/${id}`);
  },

  bulkCreate: async (
    category: string,
    options: Omit<CreateConfigurationOptionData, 'category'>[],
  ): Promise<BulkConfigurationOptionResult> => {
    return apiClient.post<BulkConfigurationOptionResult>(
      `${CONFIG_OPTIONS_BASE}/category/${encodeURIComponent(category)}/bulk`,
      { options },
    );
  },

  reorder: async (category: string, optionIds: string[]): Promise<ConfigurationOption[]> => {
    const response = await apiClient.post<BackendConfigurationOption[]>(
      `${CONFIG_OPTIONS_BASE}/category/${encodeURIComponent(category)}/reorder`,
      { optionIds },
    );
    return response.map(mapConfigurationOption);
  },
};

// =============================================================================
// NOTIFICATION TEMPLATES API
// =============================================================================

interface BackendNotificationTemplate {
  id: string;
  tenantId: string | null;
  tenant?: {
    id: string;
    code?: string | null;
    name?: string | null;
  } | null;
  category: string;
  eventType: string;
  channel: string;
  subject: string | null;
  template: string;
  variables: unknown;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

function normalizeNotificationEnum(value: string): string {
  return value.trim().replace('-', '_').toUpperCase();
}

function mapNotificationChannel(channel: string): NotificationTemplateChannel {
  const normalized = channel.trim().replace('-', '_').toLowerCase();
  if (
    normalized === 'email' ||
    normalized === 'sms' ||
    normalized === 'push' ||
    normalized === 'in_app'
  ) {
    return normalized;
  }
  return 'email';
}

function humanizeTemplateName(eventType: string): string {
  return eventType
    .split(/[_-]+/)
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1).toLowerCase()}`)
    .join(' ');
}

function mapNotificationTemplate(template: BackendNotificationTemplate): NotificationTemplate {
  return {
    id: template.id,
    key: template.eventType,
    name: template.subject ?? humanizeTemplateName(template.eventType),
    type: mapNotificationChannel(template.channel),
    category: template.category.toLowerCase(),
    subject: template.subject ?? undefined,
    template: template.template,
    variables: isRecord(template.variables)
      ? (template.variables as Record<string, string>)
      : undefined,
    isActive: template.active,
    tenantId: template.tenantId,
    tenantName: template.tenant?.name ?? undefined,
    tenantCode: template.tenant?.code ?? undefined,
    createdAt: new Date(template.createdAt),
    updatedAt: new Date(template.updatedAt),
  };
}

function mapTemplateVariables(variables?: string[]): Record<string, string> | undefined {
  if (!variables) {
    return undefined;
  }
  return Object.fromEntries(variables.map((variable) => [variable, variable]));
}

function unsupportedNotificationTemplateOperation(): never {
  throw new Error('This notification template operation is not supported by the current admin API');
}

export const notificationTemplatesApi = {
  /**
   * List all notification templates
   */
  list: async (params?: {
    type?: NotificationTemplateChannel;
    category?: string;
    tenantId?: string;
    active?: boolean;
    page?: number;
    limit?: number;
  }): Promise<{
    data: NotificationTemplate[];
    total: number;
    page: number;
    limit: number;
  }> => {
    const query = new URLSearchParams();
    if (params?.type) query.append('channel', normalizeNotificationEnum(params.type));
    if (params?.category) query.append('category', normalizeNotificationEnum(params.category));
    appendDefinedParam(query, 'tenantId', params?.tenantId);
    if (params?.active !== undefined) query.append('active', String(params.active));

    const suffix = query.toString() ? `?${query}` : '';
    const response = await apiClient.get<
      | BackendNotificationTemplate[]
      | { success: boolean; data: BackendNotificationTemplate[]; count?: number }
    >(`${TEMPLATES_BASE}${suffix}`);
    const templates = unwrapAdminEnvelope<BackendNotificationTemplate[]>(response);
    const data = templates.map(mapNotificationTemplate);
    return {
      data,
      total:
        isRecord(response) && typeof response['count'] === 'number'
          ? response['count']
          : data.length,
      page: params?.page ?? 1,
      limit: params?.limit ?? data.length,
    };
  },

  /**
   * Get template by ID
   */
  getById: async (id: string) => {
    const response = await apiClient.get<
      BackendNotificationTemplate | { success: boolean; data: BackendNotificationTemplate }
    >(`${TEMPLATES_BASE}/${id}`);
    return mapNotificationTemplate(unwrapAdminEnvelope<BackendNotificationTemplate>(response));
  },

  /**
   * Get template by key
   */
  getByKey: async (key: string) => {
    const templates = await notificationTemplatesApi.list();
    const template = templates.data.find((candidate) => candidate.key === key);
    if (!template) {
      throw new Error(`Notification template "${key}" not found`);
    }
    return template;
  },

  /**
   * Create notification template
   */
  create: async (data: {
    key: CreateNotificationTemplateData['key'];
    name: CreateNotificationTemplateData['name'];
    type: CreateNotificationTemplateData['type'];
    category: CreateNotificationTemplateData['category'];
    subject?: CreateNotificationTemplateData['subject'];
    template: CreateNotificationTemplateData['template'];
    variables?: CreateNotificationTemplateData['variables'];
    isActive?: CreateNotificationTemplateData['isActive'];
    tenantId?: CreateNotificationTemplateData['tenantId'];
  }) => {
    void data.name;
    const response = await apiClient.post<
      BackendNotificationTemplate | { success: boolean; data: BackendNotificationTemplate }
    >(`${TEMPLATES_BASE}`, {
      active: data.isActive,
      category: normalizeNotificationEnum(data.category),
      channel: normalizeNotificationEnum(data.type),
      eventType: data.key,
      subject: data.subject,
      template: data.template,
      tenantId: data.tenantId,
      variables: mapTemplateVariables(data.variables),
    });
    return mapNotificationTemplate(unwrapAdminEnvelope<BackendNotificationTemplate>(response));
  },

  /**
   * Update notification template
   */
  update: async (id: string, data: UpdateNotificationTemplateData) => {
    void data.name;
    const response = await apiClient.put<
      BackendNotificationTemplate | { success: boolean; data: BackendNotificationTemplate }
    >(`${TEMPLATES_BASE}/${id}`, {
      active: data.isActive,
      subject: data.subject,
      template: data.template,
      variables: mapTemplateVariables(data.variables),
    });
    return mapNotificationTemplate(unwrapAdminEnvelope<BackendNotificationTemplate>(response));
  },

  /**
   * Delete notification template
   */
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`${TEMPLATES_BASE}/${id}`);
  },

  /**
   * Test template with sample data
   */
  test: async (
    id: string,
    testData: Record<string, unknown>,
  ): Promise<{
    rendered: string;
    preview: string;
  }> => {
    void id;
    void testData;
    return unsupportedNotificationTemplateOperation();
  },

  /**
   * Clone template
   */
  clone: async (id: string, newKey: string) => {
    void id;
    void newKey;
    return unsupportedNotificationTemplateOperation();
  },
};

// =============================================================================
// AUDIT LOG API
// =============================================================================

export const auditLogApi = {
  /**
   * List audit logs with filtering and pagination
   */
  list: async (tenantId: string, filters?: AuditLogFilters): Promise<AuditLogListResponse> => {
    const params = new URLSearchParams();
    const page = filters?.page ?? 1;
    const limit = filters?.limit ?? 25;

    appendDefinedParam(params, 'action', filters?.action);
    appendDefinedParam(params, 'status', filters?.status);
    appendDefinedParam(params, 'startDate', filters?.startDate);
    appendDefinedParam(params, 'endDate', filters?.endDate);
    appendDefinedParam(params, 'page', page);
    appendDefinedParam(params, 'limit', limit);

    const response = await apiClient.get<AuthAuditLogListResponse>(
      `${tenantAuthAdminPath(tenantId, 'audit-logs')}?${params}`,
    );

    return {
      data: response.logs.map(mapAuthAuditLog),
      total: response.total,
      page,
      limit,
      totalPages: Math.max(1, Math.ceil(response.total / limit)),
    };
  },

  /**
   * Get audit log by ID
   */
  getById: async (tenantId: string, id: string): Promise<AuditLog> => {
    const response = await apiClient.get<AuthAuditLogEntry>(
      tenantAuthAdminPath(tenantId, `audit-logs/${encodeURIComponent(id)}`),
    );

    return mapAuthAuditLog(response);
  },

  /**
   * Get available audit actions
   */
  getActions: async (): Promise<string[]> => {
    return Object.keys(AUDIT_LOG_ACTION_LABELS);
  },

  /**
   * Get available entity types
   */
  getEntityTypes: async (): Promise<string[]> => {
    return Object.keys(AUDIT_LOG_ENTITY_LABELS);
  },

  /**
   * Get audit log statistics
   */
  getStats: async (
    tenantId: string,
    filters?: {
      days?: number;
      startDate?: string;
      endDate?: string;
    },
  ): Promise<AuthAuditStats> => {
    const params = new URLSearchParams();
    appendDefinedParam(params, 'days', filters?.days ?? 7);

    return apiClient.get<AuthAuditStats>(
      `${tenantAuthAdminPath(tenantId, 'audit-stats')}?${params}`,
    );
  },

  /**
   * Get tenant auth security alerts
   */
  getSecurityAlerts: async (tenantId: string, hours = 24): Promise<AuthSecurityAlerts> => {
    const params = new URLSearchParams();
    appendDefinedParam(params, 'hours', hours);

    return apiClient.get<AuthSecurityAlerts>(
      `${tenantAuthAdminPath(tenantId, 'security-alerts')}?${params}`,
    );
  },

  /**
   * Export audit logs to CSV/Excel
   */
  export: async (tenantId: string, filters?: AuditLogFilters): Promise<Blob> => {
    const now = new Date();
    const defaultStart = new Date(now);
    defaultStart.setDate(now.getDate() - 30);

    const params = new URLSearchParams();
    appendDefinedParam(params, 'startDate', filters?.startDate ?? defaultStart.toISOString());
    appendDefinedParam(params, 'endDate', filters?.endDate ?? now.toISOString());

    const response = await apiClient.get<AuthAuditLogExportResponse>(
      `${tenantAuthAdminPath(tenantId, 'audit-logs/export')}?${params}`,
    );

    return new Blob([buildAuditCsv(response.logs)], { type: 'text/csv;charset=utf-8' });
  },

  /**
   * Run tenant audit retention cleanup.
   */
  cleanup: async (tenantId: string): Promise<AuthAuditCleanupResponse> => {
    return apiClient.post<AuthAuditCleanupResponse>(
      tenantAuthAdminPath(tenantId, 'audit-logs/cleanup'),
      {},
    );
  },
};

// =============================================================================
// DASHBOARD & STATS API
// =============================================================================

export const dashboardApi = {
  /**
   * Get admin dashboard overview statistics
   */
  getOverview: async (): Promise<{
    tenants: {
      total: number;
      active: number;
      trial: number;
      suspended: number;
    };
    revenue: {
      thisMonth: number;
      lastMonth: number;
      growth: number;
    };
    users: {
      total: number;
      activeToday: number;
      newThisWeek: number;
    };
    system: {
      uptime: number;
      cpu: number;
      memory: number;
      storage: number;
    };
  }> => {
    return unsupportedAdminApiOperation();
  },

  /**
   * Get tenant growth metrics
   */
  getTenantGrowth: async (params?: {
    period?: 'week' | 'month' | 'quarter' | 'year';
  }): Promise<{
    labels: string[];
    data: number[];
    total: number;
    growth: number;
  }> => {
    void params;
    return unsupportedAdminApiOperation();
  },

  /**
   * Get revenue analytics
   */
  getRevenue: async (params?: {
    startDate?: string;
    endDate?: string;
    groupBy?: 'day' | 'week' | 'month';
  }): Promise<{
    total: number;
    byPlan: Record<string, number>;
    timeline: Array<{ date: string; amount: number }>;
    mrr: number;
    arr: number;
  }> => {
    void params;
    return unsupportedAdminApiOperation();
  },

  /**
   * Get plan distribution
   */
  getPlanDistribution: async (): Promise<{
    distribution: Record<string, number>;
    percentages: Record<string, number>;
  }> => {
    return unsupportedAdminApiOperation();
  },
};

// =============================================================================
// OPERATIONS API (System Health, Logs, Metrics)
// =============================================================================

export interface PlatformSystemHealth {
  status: string;
  timestamp: string;
  uptime: string;
  memory: Record<string, string>;
  cpu: {
    cores: number;
    model: string;
    loadAverage: number[];
  };
}

export interface PlatformServicesHealth {
  services: Record<string, { status: string; latency: string }>;
  overall: string;
}

export interface PlatformErrorLogs {
  message: string;
  pagination: { limit: number; offset: number };
  severity?: string;
  errors: Array<{
    id: string;
    timestamp: string;
    service: string;
    severity: string;
    message: string;
    stack: string;
  }>;
}

export interface PlatformPerformanceMetrics {
  message: string;
  timeRange: string;
  metrics: Record<string, string | number>;
}

export interface PlatformAuditLog {
  id: string;
  action: string;
  entityType: string;
  entityId: string;
  userId: string | null;
  userEmail: string | null;
  tenantId: string | null;
  tenantName: string | null;
  oldValue: unknown;
  newValue: unknown;
  metadata: Record<string, unknown>;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
  source: 'crud' | 'auth';
}

export interface PlatformAuditLogs {
  data: PlatformAuditLog[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PlatformAuditStats {
  totalLogs: number;
  logsToday: number;
  logsByAction: Record<string, number>;
  logsByEntityType: Record<string, number>;
  topUsers: Array<{ userId: string; email: string; count: number }>;
  sources: { crud: number; auth: number };
}

export interface PlatformSystemConfig {
  environment: string;
  version: string;
  services: Record<string, { port: number | null; status: string; baseUrl?: string | null }>;
  features: Record<string, boolean>;
}

export interface PlatformAuthOverview {
  totalTenants: number;
  tenantsWithMfa: number;
  tenantsWithSso: number;
  tenantsWithPasskeys: number;
  totalActiveSessions: number;
  failedLoginsToday: number;
  lockedAccounts: number;
  blockedIps: number;
}

export interface PlatformBlockedIp {
  id: string;
  ipAddress: string;
  ipRange?: string | null;
  reason?: string | null;
  blockedBy?: string | null;
  blockedAt: string | Date;
  expiresAt?: string | Date | null;
}

export interface PlatformBlockedIpsResponse {
  blockedIps: PlatformBlockedIp[];
}

export interface BlockGlobalIpInput {
  ipAddress: string;
  reason: string;
  expiresAt?: string;
}

export interface GatewayCircuitBreakerMetrics {
  name: string;
  state: string;
  failures: number;
  successes: number;
  lastFailureTime: number | null;
  totalCalls: number;
  totalFailures: number;
  totalSuccesses: number;
  failureRate: number;
  successRate: number;
}

export interface GatewayResilienceStatus {
  status: string;
  timestamp: string;
  circuitBreakers: GatewayCircuitBreakerMetrics[];
  summary: {
    total: number;
    closed: number;
    open: number;
    halfOpen: number;
  };
}

export type PlatformEventOutboxStatus = 'PENDING' | 'DISPATCHING' | 'DISPATCHED' | 'FAILED';
export type PlatformEventInboxStatus = 'PROCESSING' | 'PROCESSED' | 'FAILED';
export type PlatformNotificationDeliveryStatus =
  | 'PENDING'
  | 'SENT'
  | 'DELIVERED'
  | 'FAILED'
  | 'BOUNCED';

export interface PlatformEventOutboxItem {
  id: string;
  tenantId: string | null;
  subject: string;
  eventId: string;
  source: string;
  status: string;
  attempts: number;
  availableAt: string;
  lockedAt: string | null;
  lockedBy: string | null;
  dispatchedAt: string | null;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
  payloadPreview: string;
  headersPreview: string | null;
}

export interface PlatformEventInboxItem {
  id: string;
  tenantId: string | null;
  consumer: string;
  subject: string;
  eventId: string;
  status: string;
  attempts: number;
  lastError: string | null;
  processedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PlatformEventQueueParams {
  status?: string;
  tenantId?: string;
  subject?: string;
  source?: string;
  consumer?: string;
  search?: string;
  limit?: number;
  page?: number;
}

export interface PlatformEventQueueResponse<TItem> {
  data: TItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PlatformEventOperationsSummary {
  outbox: Record<PlatformEventOutboxStatus, number>;
  inbox: Record<PlatformEventInboxStatus, number>;
}

export interface PlatformEventOperationResult<TItem> {
  event: TItem;
  previousStatus: string;
  message: string;
}

export interface PlatformNotificationDeliverySummary {
  logs: Record<PlatformNotificationDeliveryStatus, number>;
  retryable: number;
  terminalFailures: number;
}

export interface PlatformNotificationDeliveryItem {
  id: string;
  tenantId: string;
  notificationId: string | null;
  channel: string;
  recipient: string;
  subject: string | null;
  status: string;
  retryCount: number;
  errorMessage: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
  failedAt: string | null;
  createdAt: string;
  eventType: string | null;
  hasReplayPayload: boolean;
  metadataPreview: string | null;
}

export interface PlatformNotificationDeliveryParams {
  status?: string;
  tenantId?: string;
  channel?: string;
  search?: string;
  limit?: number;
  page?: number;
}

export interface PlatformNotificationRetryResult {
  delivery: PlatformNotificationDeliveryItem;
  outboxEvent: PlatformEventOutboxItem;
  previousStatus: string;
  message: string;
}

export interface PlatformAbacTenantSyncResult {
  tenantId: string;
  tenantName: string;
  policiesCreated: number;
  rolesCreated: number;
  assignmentsCreated: number;
  permissionCachesInvalidated: number;
}

export interface PlatformAbacAllTenantsSyncResult {
  totalTenants: number;
  succeeded: number;
  failed: number;
  results: Array<{
    tenantId: string;
    tenantName: string;
    status: 'success' | 'error';
    policiesCreated?: number;
    rolesCreated?: number;
    assignmentsCreated?: number;
    permissionCachesInvalidated?: number;
    error?: string;
  }>;
}

export const operationsApi = {
  /**
   * Get system health status
   */
  getSystemHealth: async (): Promise<PlatformSystemHealth> => {
    const response = await apiClient.get<PlatformSystemHealth>(
      `${ADMIN_BASE}/operations/health/system`,
    );
    return response;
  },

  /**
   * Get individual service health
   */
  getServicesHealth: async (): Promise<PlatformServicesHealth> => {
    const response = await apiClient.get<PlatformServicesHealth>(
      `${ADMIN_BASE}/operations/health/services`,
    );
    return response;
  },

  /**
   * Get global platform audit logs
   */
  getAuditLogs: async (params?: {
    limit?: number;
    page?: number;
    action?: string;
    entityType?: string;
    search?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<PlatformAuditLogs> => {
    const query = new URLSearchParams();
    appendDefinedParam(query, 'limit', params?.limit);
    appendDefinedParam(query, 'page', params?.page);
    appendDefinedParam(query, 'action', params?.action);
    appendDefinedParam(query, 'entityType', params?.entityType);
    appendDefinedParam(query, 'search', params?.search);
    appendDefinedParam(query, 'startDate', params?.startDate);
    appendDefinedParam(query, 'endDate', params?.endDate);

    const response = await apiClient.get<PlatformAuditLogs>(
      withQuery(`${ADMIN_BASE}/operations/logs/audit`, query),
    );
    return response;
  },

  /**
   * Get global platform audit statistics
   */
  getAuditLogStats: async (params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<PlatformAuditStats> => {
    const query = new URLSearchParams();
    appendDefinedParam(query, 'startDate', params?.startDate);
    appendDefinedParam(query, 'endDate', params?.endDate);

    const response = await apiClient.get<PlatformAuditStats>(
      withQuery(`${ADMIN_BASE}/operations/logs/audit/stats`, query),
    );
    return response;
  },

  /**
   * Get error logs
   */
  getErrorLogs: async (params?: {
    limit?: number;
    offset?: number;
    severity?: 'error' | 'warning' | 'critical';
  }): Promise<PlatformErrorLogs> => {
    const query = new URLSearchParams();
    if (params?.limit) query.append('limit', String(params.limit));
    if (params?.offset) query.append('offset', String(params.offset));
    if (params?.severity) query.append('severity', params.severity);

    const response = await apiClient.get<PlatformErrorLogs>(
      withQuery(`${ADMIN_BASE}/operations/logs/errors`, query),
    );
    return response;
  },

  /**
   * Get performance metrics
   */
  getPerformanceMetrics: async (params?: {
    timeRange?: '15m' | '1h' | '6h' | '24h' | '7d';
  }): Promise<PlatformPerformanceMetrics> => {
    const query = new URLSearchParams();
    if (params?.timeRange) query.append('timeRange', params.timeRange);

    const response = await apiClient.get<PlatformPerformanceMetrics>(
      withQuery(`${ADMIN_BASE}/operations/metrics/performance`, query),
    );
    return response;
  },

  /**
   * Get system configuration
   */
  getSystemConfig: async (): Promise<PlatformSystemConfig> => {
    const response = await apiClient.get<PlatformSystemConfig>(
      `${ADMIN_BASE}/operations/system/config`,
    );
    return response;
  },

  /**
   * Get Gateway circuit-breaker status
   */
  getGatewayCircuitBreakers: async (): Promise<GatewayResilienceStatus> => {
    const response = await apiClient.get<GatewayResilienceStatus>('/resilience/circuit-breakers');
    return response;
  },

  /**
   * Get global authentication operations overview
   */
  getAuthOverview: async (): Promise<PlatformAuthOverview> => {
    const response = await apiClient.get<PlatformAuthOverview>(`${AUTH_ADMIN_BASE}/overview`);
    return response;
  },

  /**
   * Get globally blocked IP addresses
   */
  getGlobalBlockedIps: async (): Promise<PlatformBlockedIp[]> => {
    const response = await apiClient.get<PlatformBlockedIpsResponse>(
      `${AUTH_ADMIN_BASE}/global/blocked-ips`,
    );
    return response.blockedIps.map((ip) => ({
      ...ip,
      blockedAt: new Date(ip.blockedAt),
      expiresAt: ip.expiresAt ? new Date(ip.expiresAt) : null,
    }));
  },

  /**
   * Block an IP address globally
   */
  blockGlobalIp: async (input: BlockGlobalIpInput): Promise<void> => {
    await apiClient.post(`${AUTH_ADMIN_BASE}/global/blocked-ips`, input);
  },

  /**
   * Unblock a globally blocked IP address
   */
  unblockGlobalIp: async (ipAddress: string): Promise<void> => {
    await apiClient.delete(
      `${AUTH_ADMIN_BASE}/global/blocked-ips/${encodeURIComponent(ipAddress)}`,
    );
  },

  /**
   * Get integration event recovery queue summary
   */
  getEventOperationsSummary: async (): Promise<PlatformEventOperationsSummary> => {
    const response = await apiClient.get<PlatformEventOperationsSummary>(
      `${ADMIN_BASE}/operations/events/summary`,
    );
    return response;
  },

  /**
   * List integration outbox rows for operator recovery
   */
  getEventOutbox: async (
    params?: PlatformEventQueueParams,
  ): Promise<PlatformEventQueueResponse<PlatformEventOutboxItem>> => {
    const query = new URLSearchParams();
    appendDefinedParam(query, 'status', params?.status);
    appendDefinedParam(query, 'tenantId', params?.tenantId);
    appendDefinedParam(query, 'subject', params?.subject);
    appendDefinedParam(query, 'source', params?.source);
    appendDefinedParam(query, 'search', params?.search);
    appendDefinedParam(query, 'limit', params?.limit);
    appendDefinedParam(query, 'page', params?.page);

    const response = await apiClient.get<PlatformEventQueueResponse<PlatformEventOutboxItem>>(
      withQuery(`${ADMIN_BASE}/operations/events/outbox`, query),
    );
    return response;
  },

  /**
   * List integration inbox rows for consumer retry diagnostics
   */
  getEventInbox: async (
    params?: PlatformEventQueueParams,
  ): Promise<PlatformEventQueueResponse<PlatformEventInboxItem>> => {
    const query = new URLSearchParams();
    appendDefinedParam(query, 'status', params?.status);
    appendDefinedParam(query, 'tenantId', params?.tenantId);
    appendDefinedParam(query, 'subject', params?.subject);
    appendDefinedParam(query, 'consumer', params?.consumer);
    appendDefinedParam(query, 'search', params?.search);
    appendDefinedParam(query, 'limit', params?.limit);
    appendDefinedParam(query, 'page', params?.page);

    const response = await apiClient.get<PlatformEventQueueResponse<PlatformEventInboxItem>>(
      withQuery(`${ADMIN_BASE}/operations/events/inbox`, query),
    );
    return response;
  },

  /**
   * Requeue an outbox event for dispatcher replay
   */
  replayOutboxEvent: async (
    id: string,
  ): Promise<PlatformEventOperationResult<PlatformEventOutboxItem>> => {
    const response = await apiClient.post<PlatformEventOperationResult<PlatformEventOutboxItem>>(
      `${ADMIN_BASE}/operations/events/outbox/${id}/replay`,
    );
    return response;
  },

  /**
   * Release an inbox row so a replayed source event can retry processing
   */
  releaseInboxEvent: async (
    id: string,
  ): Promise<PlatformEventOperationResult<PlatformEventInboxItem>> => {
    const response = await apiClient.post<PlatformEventOperationResult<PlatformEventInboxItem>>(
      `${ADMIN_BASE}/operations/events/inbox/${id}/release`,
    );
    return response;
  },

  /**
   * Get notification delivery retry and terminal-failure summary
   */
  getNotificationDeliverySummary: async (): Promise<PlatformNotificationDeliverySummary> => {
    const response = await apiClient.get<PlatformNotificationDeliverySummary>(
      `${ADMIN_BASE}/operations/events/notifications/summary`,
    );
    return response;
  },

  /**
   * List notification delivery logs for operator recovery
   */
  getNotificationDeliveries: async (
    params?: PlatformNotificationDeliveryParams,
  ): Promise<PlatformEventQueueResponse<PlatformNotificationDeliveryItem>> => {
    const query = new URLSearchParams();
    appendDefinedParam(query, 'status', params?.status);
    appendDefinedParam(query, 'tenantId', params?.tenantId);
    appendDefinedParam(query, 'channel', params?.channel);
    appendDefinedParam(query, 'search', params?.search);
    appendDefinedParam(query, 'limit', params?.limit);
    appendDefinedParam(query, 'page', params?.page);

    const response = await apiClient.get<
      PlatformEventQueueResponse<PlatformNotificationDeliveryItem>
    >(withQuery(`${ADMIN_BASE}/operations/events/notifications/deliveries`, query));
    return response;
  },

  /**
   * Queue a notification delivery retry through the integration outbox
   */
  retryNotificationDelivery: async (id: string): Promise<PlatformNotificationRetryResult> => {
    const response = await apiClient.post<PlatformNotificationRetryResult>(
      `${ADMIN_BASE}/operations/events/notifications/deliveries/${id}/retry`,
    );
    return response;
  },

  /**
   * Re-bootstrap ABAC for a specific tenant
   */
  syncAbacForTenant: async (tenantId: string): Promise<PlatformAbacTenantSyncResult> => {
    const response = await apiClient.post<PlatformAbacTenantSyncResult>(
      `${ADMIN_BASE}/operations/abac/bootstrap/${tenantId}`,
    );
    return response;
  },

  /**
   * Sync ABAC permissions for all active tenants
   */
  syncAbacForAllTenants: async (): Promise<PlatformAbacAllTenantsSyncResult> => {
    const response = await apiClient.post<PlatformAbacAllTenantsSyncResult>(
      `${ADMIN_BASE}/operations/abac/sync-all`,
    );
    return response;
  },
};

// =============================================================================
// ZEPTO PLATFORM CONFIG API (Phase 2.1B.1)
// =============================================================================
//
// Platform-wide Zepto static-header auth pair (Phase 2.6.2.A tenantless
// `/zeptomail-webhook/events` route). SuperAdmin generates + rotates the
// header value here, then pastes it into Zepto's dashboard. In-flight
// deliveries carrying the OLD value continue to authenticate for 24h
// after rotation thanks to the grace window (Phase 2.1B).

const ZEPTO_PLATFORM_BASE = '/admin/config/zepto/platform';

export interface ZeptoPlatformStatus {
  authHeaderName: string | null;
  authHeaderValuePreview: string | null;
  authHeaderValuePrevPreview: string | null;
  authHeaderValuePrevValidUntil: string | null;
}

export interface ZeptoPlatformRotationResult {
  value: string;
  previousValueValidUntil: string | null;
}

export const zeptoPlatformApi = {
  /**
   * Get masked previews of the platform Zepto auth config + rotation grace
   * state. Never returns the plaintext; only the last 4 chars.
   */
  getStatus: async (): Promise<ZeptoPlatformStatus> => {
    const response = await apiClient.get<ZeptoPlatformStatus>(`${ZEPTO_PLATFORM_BASE}/status`);
    return response;
  },

  /**
   * Rotate the platform-wide Zepto static-header auth value. The returned
   * `value` is the new plaintext — show it ONCE to the SuperAdmin so they
   * can paste it into Zepto's dashboard. Subsequent status reads see only
   * masked previews; the plaintext is unrecoverable after this call.
   *
   * The previous value remains valid until `previousValueValidUntil` (24h
   * grace) — in-flight Zepto webhooks carrying the old value continue to
   * authenticate during this window.
   */
  rotateAuthHeader: async (): Promise<ZeptoPlatformRotationResult> => {
    const response = await apiClient.post<ZeptoPlatformRotationResult>(
      `${ZEPTO_PLATFORM_BASE}/rotate-auth-header`,
    );
    return response;
  },
};

// =============================================================================
// EXPORT
// =============================================================================

export const adminService = {
  tenants: tenantsApi,
  globalUsers: globalUsersApi,
  schema: schemaApi,
  featureFlags: featureFlagsApi,
  systemConfig: systemConfigApi,
  configurationOptions: configurationOptionsApi,
  notificationTemplates: notificationTemplatesApi,
  auditLogs: auditLogApi,
  dashboard: dashboardApi,
  operations: operationsApi,
  zeptoPlatform: zeptoPlatformApi,
};

export default adminService;
